import { ApiError } from './api.js';

/**
 * 064: the outcome of a read, as something a screen cannot ignore.
 *
 * `useFetch` used to return `{ data, loading, error }`, and eleven of its fourteen call sites
 * destructured `{ data, loading }` — so a failure rendered as the screen's empty copy, and a
 * backend outage was indistinguishable from an empty fleet. A field that can be omitted is how
 * that survived fourteen call sites; a discriminated union cannot be omitted.
 *
 * `data` is null on every non-`ok` arm on purpose: "content beside a failure" must be
 * unrepresentable, including for `gone` — a screen must not keep showing a car it has just been
 * told no longer exists.
 */
export type FetchState<T> =
  | { status: 'idle'; data: null }
  | { status: 'loading'; data: null }
  | { status: 'ok'; data: T }
  | { status: 'gone'; data: null; reason: string }
  | { status: 'unauthenticated'; data: null; reason: string }
  | { status: 'refused'; data: null; reason: string }
  | { status: 'failed'; data: null; reason: string };

/** The states a rejection can produce. Every one of them ends a read. */
export type FetchErrorStatus = 'gone' | 'unauthenticated' | 'refused' | 'failed';

/**
 * What we say when the platform said nothing.
 *
 * `fetch` rejects with a TypeError before any response exists — a dead network, a refused
 * connection, an offline device — and the browser's own wording ("Failed to fetch") is not a
 * reason a customer can act on.
 */
export const NETWORK_FAILURE_REASON = 'Could not reach the service. Check your connection.';

/**
 * Classify a rejection by the status the platform answered with — never by its message text.
 *
 * Only 404, 401 and 403 are terminal: retrying them cannot succeed, which is why the states they
 * produce are the ones that offer no retry. Everything else, including a 409 or a 500, is a
 * failure worth trying again.
 */
export function classifyFetchError(err: unknown): { status: FetchErrorStatus; reason: string } {
  if (err instanceof ApiError) {
    // ApiError's message is never empty: `refusalReason` falls back before constructing it.
    if (err.status === 404) return { status: 'gone', reason: err.message };
    if (err.status === 401) return { status: 'unauthenticated', reason: err.message };
    if (err.status === 403) return { status: 'refused', reason: err.message };
    return { status: 'failed', reason: err.message };
  }

  return { status: 'failed', reason: NETWORK_FAILURE_REASON };
}
